import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faSpinner, faPersonWalkingDashedLineArrowRight } from '@fortawesome/free-solid-svg-icons';


export interface ColumnDef<T> {
    key: string;
    label: string;
    sortable?: boolean;
    render?: (item: T) => React.ReactNode;
    headerRender?: () => React.ReactNode;
    cellClassName?: string | ((item: T) => string);
    headerClassName?: string;
}


export interface RowSelectionConfig<T> {
    selectedIds: number[];
    onSelect: (item: T) => void;
    onSelectAll: () => void;
    isSelectable: (item: T) => boolean;
    getRowId: (item: T) => number;
    disabledTitle?: string;
    selectAllDisabled?: boolean;
    totalCount?: number;
}


export interface DataTableProps<T> {
    data: T[];
    columns: ColumnDef<T>[];
    loading?: boolean;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
    loadingSpinner?: React.ReactNode;
    onSort?: (field: string) => void;
    rowKey: (item: T, index: number) => string;
    rowClassName?: (item: T) => string;
    renderActions?: (item: T) => React.ReactNode;
    actionsLabel?: string;
    rowSelection?: RowSelectionConfig<T>;
    loadingText?: string;
    emptyTitle?: string;
    emptyMessage?: string;
    emptyHint?: string;
    className?: string;
    headerActions?: React.ReactNode;
    onRowClick?: (item: T) => void;
}


function DataTable<T>({
    data,
    columns,
    loading = false,
    sortField,
    sortDirection,
    onSort,
    rowKey,
    rowClassName,
    renderActions,
    actionsLabel = 'Ações',
    rowSelection,
    loadingSpinner = <FontAwesomeIcon icon={faSpinner} spin size="2x" />,
    emptyTitle = 'Nenhum registro encontrado',
    emptyMessage = 'Não há registros disponíveis com os filtros aplicados.',
    emptyHint = 'Tente ajustar os filtros ou limpar para ver todos os registros.',
    className = 'table-container',
    headerActions,
    onRowClick,
}: DataTableProps<T>) {


    const getSortIcon = (field: string) => {
        if (sortField !== field) return faSort;
        return sortDirection === 'asc' ? faSortUp : faSortDown;
    };

    const renderSortableHeader = (col: ColumnDef<T>) => {
        const content = col.headerRender ? col.headerRender() : col.label;
        if (!col.sortable || !onSort) {
            return <th key={col.key} className={col.headerClassName}>{content}</th>;
        }

        return (
            <th
                key={col.key}
                className={col.headerClassName}
                onClick={() => onSort(col.key)}
                style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                title={`Ordenar por ${col.label}`}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    {content}
                    <FontAwesomeIcon icon={getSortIcon(col.key)} style={{ fontSize: '0.8em' }} />
                </span>
            </th>
        );
    };

    const resolveCellClass = (col: ColumnDef<T>, item: T): string => {
        if (!col.cellClassName) return '';
        return typeof col.cellClassName === 'function'
            ? col.cellClassName(item)
            : col.cellClassName;
    };

    const selectableCount = rowSelection
        ? data.filter(item => rowSelection.isSelectable(item)).length
        : 0;

    const totalCount = rowSelection?.totalCount ?? selectableCount;

    const allSelected =
        !!rowSelection &&
        rowSelection.selectedIds.length > 0 &&
        rowSelection.selectedIds.length === totalCount;

    if (loading) {
        return (
           <div className="loading-state" aria-live="polite" aria-busy="true">
                {loadingSpinner}
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="empty-state-container">
                <svg
                    className="empty-state-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M9 11H15M9 15H15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path d="M9 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <h3 className="empty-state-title">{emptyTitle}</h3>
                <p className="empty-state-message">{emptyMessage}</p>
                <p className="empty-state-hint">{emptyHint}</p>
            </div>
        );
    }

    return (
        <div className={className}>
            {headerActions && (
                <div className="datatable-header-actions">
                    {headerActions}
                </div>
            )}
            <table className="fiori-table">
                <thead>
                    <tr>
                        {rowSelection && (
                            <th className="checkbox-column">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={rowSelection.onSelectAll}
                                    disabled={selectableCount === 0 || !!rowSelection.selectAllDisabled}
                                    title="Selecionar todos"
                                />
                            </th>
                        )}

                        {columns.map(col => renderSortableHeader(col))}

                        {renderActions && (
                            <th className="action-column">{actionsLabel}</th>
                        )}
                    </tr>
                </thead>

                <tbody>
                    {data.map((item, index) => {
                        const selectable = rowSelection?.isSelectable(item) ?? false;
                        const rowId = rowSelection?.getRowId(item);
                        const isSelected = rowSelection
                            ? rowSelection.selectedIds.includes(rowId!)
                            : false;

                        return (
                            <tr
                                key={rowKey(item, index)}
                                className={`table-row${rowClassName ? ` ${rowClassName(item)}` : ''}`}
                                onClick={onRowClick ? () => onRowClick(item) : undefined}
                                style={onRowClick ? { cursor: 'pointer' } : undefined}
                            >
                                {rowSelection && (
                                    <td className="checkbox-column">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => rowSelection.onSelect(item)}
                                            disabled={!selectable}
                                            title={
                                                selectable
                                                    ? 'Selecionar'
                                                    : (rowSelection.disabledTitle ?? 'Não disponível')
                                            }
                                        />
                                    </td>
                                )}

                                {columns.map(col => (
                                    <td
                                        key={col.key}
                                        className={resolveCellClass(col, item)}
                                    >
                                        {col.render
                                            ? col.render(item)
                                            : String((item as any)[col.key] ?? '')}
                                    </td>
                                ))}

                                {renderActions && (
                                    <td className="action-column">
                                        {renderActions(item)}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default DataTable;
